// The one toast at the bottom of the page (FlashToast.vue shows it). A result
// of the member's own click goes here: "Saved", a refusal, a change with an
// Undo. A new toast replaces the one on screen.
import { ref } from 'vue'

export type ToastTone = 'success' | 'error'

export type Toast = {
  id: number
  text: string
  tone: ToastTone
  action?: { label: string; run: () => void }
}

type ToastInput = Omit<Toast, 'id' | 'tone'> & {
  tone?: ToastTone
  duration?: number
}

const SUCCESS_MS = 5000
// A refusal, or a toast with an action, needs more time to read.
const LONG_MS = 9000
// After the mouse leaves a held toast.
const RELEASE_MS = 2500

const toast = ref<Toast | null>(null)
let timer: ReturnType<typeof setTimeout> | undefined
let nextId = 1

export const dismissToast = () => {
  clearTimeout(timer)
  toast.value = null
}

export const showToast = (input: ToastInput) => {
  const tone = input.tone ?? 'success'
  toast.value = {
    id: nextId++,
    text: input.text,
    tone,
    action: input.action,
  }
  clearTimeout(timer)
  timer = setTimeout(
    dismissToast,
    input.duration ?? (input.action || tone === 'error' ? LONG_MS : SUCCESS_MS),
  )
}

// The toast stays while the mouse is on it.
export const holdToast = () => clearTimeout(timer)
export const releaseToast = () => {
  clearTimeout(timer)
  timer = setTimeout(dismissToast, RELEASE_MS)
}

export const useToast = () => ({
  toast,
  showToast,
  dismissToast,
  holdToast,
  releaseToast,
})
